// Exportable de viajes del proveedor (LQ-1, ADR-026 §6.2): las filas del
// periodo de liquidacion que contiene la fecha elegida, con vista previa y
// descarga en CSV o XLSX. Es el mismo archivo que baja la planta, asi los dos
// lados concilian sobre lo mismo. Sin montos (regla 7): cada lado le pone
// precio en su sistema.
//
// Una planta por vez: la seccion la recibe ya elegida del filtro del portal.
// La fecha por defecto es hoy en la zona de la planta, leida de la API
// (regla 9); el navegador no decide que dia es. El periodo lo fija la consola
// de UCORP por par planta y proveedor; aca solo se elige un dia dentro de el.
import { useEffect, useId, useRef, useState } from "react";
import type { TripExportResponse } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { Card, PanelHeader } from "../components/ui/card.js";
import { Failure } from "../components/ui/failure.js";
import { Icon } from "../components/ui/icon.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { SkeletonTable } from "../components/ui/skeleton.js";
import { formatCalendarDate } from "../logic/documents.js";
import {
  OUTCOME_LABEL,
  TRIP_EXPORT_PREVIEW_ROWS,
  actualKmText,
  costCenterText,
  isCalendarDate,
  ownExport,
  passengersText,
  periodTexts,
  plannedKmText,
  serviceLabel,
  tripCount,
  waitText,
} from "../logic/trip-export.js";
import { TripExportError, type TripExportPort } from "./port.js";

/** La planta de la seccion: una por `siteId`, con los proveedores de sus habilitaciones. */
export interface TripExportSite {
  readonly siteId: string;
  readonly name: string;
  readonly providerIds: readonly string[];
}

/** Entrega el archivo al navegador. Los tests lo reemplazan: jsdom no descarga. */
export type SaveFile = (blob: Blob, fileName: string) => void;

export const saveWithAnchor: SaveFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // El click ya tomo la URL; se libera en el proximo turno para no cortar la descarga.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

interface Props {
  session: PortalSession;
  port: TripExportPort;
  site: TripExportSite;
  save?: SaveFile;
}

type TodayState = { estado: "cargando" } | { estado: "listo" } | { estado: "error"; kind: TripExportError["kind"] };

type ViewState =
  | { estado: "inicial" }
  | { estado: "cargando" }
  | { estado: "listo"; date: string; data: TripExportResponse }
  | { estado: "error"; kind: TripExportError["kind"] };

export const ACCESS_COPY = "Tu empresa no tiene habilitación activa en esta planta.";

function kindOf(error: unknown): TripExportError["kind"] {
  return error instanceof TripExportError ? error.kind : "unavailable";
}

function readCopy(kind: TripExportError["kind"]): string {
  if (kind === "access") return ACCESS_COPY;
  return "No se pudo consultar el exportable. Reintentar en unos minutos.";
}

function downloadCopy(kind: TripExportError["kind"]): string {
  if (kind === "access") return ACCESS_COPY;
  return "No se pudo descargar el archivo. Reintentar en unos minutos.";
}

export function TripExport({ session, port, site, save = saveWithAnchor }: Props): React.JSX.Element {
  const baseId = useId();
  const [date, setDate] = useState("");
  const [today, setToday] = useState<TodayState>({ estado: "cargando" });
  const [view, setView] = useState<ViewState>({ estado: "inicial" });
  const [downloading, setDownloading] = useState<"csv" | "xlsx" | null>(null);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  // Solo la ultima consulta pinta: un "Ver" viejo que contesta tarde no pisa al nuevo.
  const lastRead = useRef(0);

  useEffect(() => {
    let current = true;
    port.today(session, site.siteId).then(
      (result) => {
        if (!current) return;
        setDate((previous) => (previous === "" ? result.date : previous));
        setToday({ estado: "listo" });
      },
      (error: unknown) => {
        if (current) setToday({ estado: "error", kind: kindOf(error) });
      },
    );
    return () => {
      current = false;
    };
  }, [port, session, site.siteId]);

  const valid = isCalendarDate(date);

  function read(): void {
    if (!isCalendarDate(date)) return;
    lastRead.current += 1;
    const ticket = lastRead.current;
    setView({ estado: "cargando" });
    port.read(session, site.siteId, date).then(
      (data) => {
        if (ticket !== lastRead.current) return;
        setView({ estado: "listo", date, data: ownExport(data, site.providerIds) });
      },
      (error: unknown) => {
        if (ticket === lastRead.current) setView({ estado: "error", kind: kindOf(error) });
      },
    );
  }

  async function download(format: "csv" | "xlsx"): Promise<void> {
    if (!isCalendarDate(date)) return;
    setDownloading(format);
    setDownloadError(undefined);
    try {
      const file = await port.download(session, site.siteId, date, format);
      save(file.blob, file.fileName);
    } catch (error) {
      setDownloadError(downloadCopy(kindOf(error)));
    } finally {
      setDownloading(null);
    }
  }

  const accessDenied = today.estado === "error" && today.kind === "access";

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div>
        <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">{site.name}</p>
        <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
          Exportable de viajes
        </h1>
        <p className="m-0 text-xs text-texto-suave">
          Viajes del período con kilómetros, esperas, pasajeros y resultado, sin montos. Es el mismo archivo que baja la planta.
        </p>
      </div>

      {accessDenied && <Failure title={ACCESS_COPY} />}
      {today.estado === "error" && !accessDenied && (
        <Alert variant="aviso">No se pudo leer el día de hoy de la planta. Elegir la fecha a mano.</Alert>
      )}

      <Card className="gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${baseId}-date`}>Fecha del período</Label>
            <Input
              id={`${baseId}-date`}
              type="date"
              className="w-auto"
              value={date}
              disabled={today.estado === "cargando"}
              aria-invalid={date !== "" && !valid}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          <Button disabled={!valid || view.estado === "cargando"} onClick={read}>
            Ver
          </Button>
          <Button variant="secundario" disabled={!valid || downloading !== null} onClick={() => void download("csv")}>
            <Icon name="exportable" />
            {downloading === "csv" ? "Descargando..." : "Descargar CSV"}
          </Button>
          <Button variant="secundario" disabled={!valid || downloading !== null} onClick={() => void download("xlsx")}>
            <Icon name="exportable" />
            {downloading === "xlsx" ? "Descargando..." : "Descargar XLSX"}
          </Button>
        </div>
        <p className="m-0 text-xs text-texto-suave">
          Cualquier día del período sirve: el período (mensual, quincenal o semanal) lo fija UCORP para la empresa en esta planta. Cada descarga queda registrada.
        </p>
        {downloadError !== undefined && <Failure title={downloadError} />}
      </Card>

      {view.estado === "cargando" && <SkeletonTable rows={4} cols={6} label="Consultando el exportable" />}
      {view.estado === "error" && <Failure title={readCopy(view.kind)} />}
      {view.estado === "listo" && <Preview id={`${baseId}-result`} date={view.date} data={view.data} />}
    </section>
  );
}

function Preview({ id, date, data }: { id: string; date: string; data: TripExportResponse }): React.JSX.Element {
  const periods = periodTexts(data.periods);
  const trips = tripCount(data.rows);
  const shown = data.rows.slice(0, TRIP_EXPORT_PREVIEW_ROWS);
  const withCostCenter = data.rows.some((row) => row.costCenterId !== undefined);

  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title={`Período que contiene el ${formatCalendarDate(date)}`} />
      <dl className="tabular m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-b border-divisor px-3.5 py-3 text-sm">
        <dt className="text-texto-suave">{periods.length > 1 ? "Períodos" : "Período"}</dt>
        <dd className="m-0 text-texto">{periods.length === 0 ? "sin período" : periods.join("; ")}</dd>
        <dt className="text-texto-suave">Viajes</dt>
        <dd className="m-0 text-texto">{trips}</dd>
        <dt className="text-texto-suave">Sin sellar todavía</dt>
        <dd className="m-0 text-texto">{data.unsealedTrips}</dd>
      </dl>
      {data.unsealedTrips > 0 && (
        <p className="m-0 px-3.5 pt-2 text-xs text-texto-suave">
          Viajes todavía en curso o sin cierre del día; aparecen después del cierre diario.
        </p>
      )}

      {data.rows.length === 0 ? (
        <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
          No hay viajes sellados en este período.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-divisor">
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-texto-suave">Fecha</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Viaje</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Servicio</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Resultado</th>
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-right text-texto-suave">Km planificados</th>
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-right text-texto-suave">Km reales</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Espera</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Pasajeros (asignados / a bordo / ausentes)</th>
                  {withCostCenter && <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-texto-suave">Centro de costos</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={`${row.tripId}:${row.costCenterId ?? ""}`} className="h-fila border-b border-divisor text-texto-2 last:border-b-0 hover:bg-velo">
                    <td className="tabular whitespace-nowrap px-3.5 py-1">{formatCalendarDate(row.serviceDate)}</td>
                    <td className="tabular px-3.5 py-1 text-xs">{row.tripId}</td>
                    <td className="px-3.5 py-1">{serviceLabel(row)}</td>
                    <td className="px-3.5 py-1">{OUTCOME_LABEL[row.outcome]}</td>
                    <td className="tabular px-3.5 py-1 text-right">{plannedKmText(row)}</td>
                    <td className="tabular px-3.5 py-1 text-right">{actualKmText(row)}</td>
                    <td className="tabular whitespace-nowrap px-3.5 py-1">{waitText(row)}</td>
                    <td className="tabular px-3.5 py-1">{passengersText(row)}</td>
                    {withCostCenter && <td className="whitespace-nowrap px-3.5 py-1">{costCenterText(row) ?? ""}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.rows.length > shown.length && (
            <p className="m-0 border-t border-divisor px-3.5 py-2 text-xs text-texto-suave">
              Se muestran las primeras {shown.length} de {data.rows.length} filas. El archivo trae todas.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
